import React, { useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import GrowthIndicator from "../common/GrowthIndicator";
import LineGraph from "../common/LineGraph";
import InfoIcon from "@material-ui/icons/Info";
import {
  IdleReportsHookParams,
  useIdlingDataForDrivers,
} from "../hooks/useIdleReportsHooks";
import type { AllDriversDto } from "../../../common/data/service-proxies";
import "../fuel-economy-report/FuelEconomyReport.css";

const formatNumber = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) {
    return "-";
  }
  return value.toFixed(2);
};

/** Backend returns time in seconds; convert to minutes for display. */
const secondsToMinutes = (seconds: number | null | undefined): number | null =>
  seconds != null && !Number.isNaN(seconds) ? seconds / 60 : null;

export const IdleReportDriverTable: React.FC<IdleReportsHookParams> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, loading: isLoading, error } = useIdlingDataForDrivers({
    fromDate,
    toDate,
    periodType,
    skipCount: page * rowsPerPage,
    maxResultCount: rowsPerPage,
  });
  const isError = !!error;
  const rows = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const onPageChange = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);
  const onRowsPerPageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
    },
    [],
  );

  const formatMin = (seconds: number | null | undefined) => {
    const min = secondsToMinutes(seconds);
    return min != null ? `${formatNumber(min)} min` : "-";
  };

  return (
    <Card className="widget">
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("idleReport.driversTable", {
            _: "Idle report by driver",
          })}
        </div>
      </div>
      <CardContent style={{ padding: 16 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !rows.length ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : (
          <TableContainer style={{ overflow: "auto" }}>
            <Table size="small" style={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {translate("common.truck", { _: "Truck" })}
                  </TableCell>
                  <TableCell>
                    {translate("common.driver", { _: "Driver" })}
                  </TableCell>
                  <TableCell>
                    {translate("idleReport.totalIdle", { _: "Total idle" })}
                  </TableCell>
                  <TableCell>
                    {translate("idleReport.offDuty", { _: "Off duty" })}
                  </TableCell>
                  <TableCell>
                    {translate("idleReport.onDuty", { _: "On duty" })}
                  </TableCell>
                  <TableCell>
                    {translate("idleReport.maxIdle", { _: "Max idle" })}
                  </TableCell>
                  <TableCell>
                    {translate("idleReport.minIdle", { _: "Min idle" })}
                  </TableCell>
                  <TableCell>
                    {translate("idleReport.avgIdle", { _: "Avg idle" })}
                  </TableCell>
                  <TableCell>
                    {translate("idleReport.avgFuelConsumption", {
                      _: "Avg. fuel consumpt.",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("idleReport.totalFuelConsumption", {
                      _: "Total fuel consumption",
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {translate("fuelEconomyReport.performance", {
                      _: "Performance",
                    })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row: AllDriversDto, index: number) => (
                  <TableRow
                    key={`${row.vehicleName ?? ""}-${
                      row.driverFullName ?? ""
                    }-${row.id ?? index}`}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.vehicleName ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.driverFullName ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formatMin(row.totalIdleTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formatMin(row.offDutyIdleValue)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formatMin(row.onDutyIdleValue)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formatMin(row.maxIdleTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formatMin(row.minIdleTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {formatMin(row.averageIdleTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.fuelConsumptionValueAverage != null
                          ? `${formatNumber(
                              row.fuelConsumptionValueAverage,
                            )} gal`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.fuelConsumptionValueTotal != null
                          ? `${formatNumber(row.fuelConsumptionValueTotal)} gal`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        {row.eventScale && row.eventScale.length > 0 && (
                          <LineGraph
                            data={row.eventScale}
                            width={115}
                            height={24}
                            isGrowing={row.isGrowing ?? false}
                            isReverse={true}
                          />
                        )}
                        {row.growth !== undefined && row.growth !== null && (
                          <GrowthIndicator
                            growth={row.growth}
                            isGrowing={row.isGrowing ?? false}
                            isReverse={true}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={translate("common.rowsPerPage", {
                _: "Rows per page",
              })}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} ${translate("common.of", { _: "of" })} ${
                  count !== -1 ? count : `more than ${to}`
                }`
              }
            />
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default IdleReportDriverTable;
